import { useMemo, useState, useRef } from "react";
import { Nav, NavItem, NavLink, TabContent, TabPane, Button, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { getActiveColorStyle } from "../../utils/colorUtils";
import UserDefinedSecurityTabs from "../../components/UserDefined/UserDefinedSecurityTabs";
import SInventoryTabs from "./components/SInventoryTabs";
import SUserDefinedFieldsTabs from "./components/SUserDefinedFieldsTabs";
import SaprTab from "./components/SaprTab";
import { useCreateTelantAdmin } from "api/tenant/service";
import { useParams } from "react-router-dom";

const Institution = ({ activeColor = "secondary", account, tenantData, onNext, onPrev, showNext, isTheme2 = false, selectStyles, inputStyle, inputClassName }) => {
    const { id: code } = useParams();
    const createTenantAdminMutation = useCreateTelantAdmin();

    const inventoryRef = useRef();
    const aprRef = useRef();
    const udfRef = useRef();

    const handleUpdate = async () => {
        const inventoryData = inventoryRef.current?.getData();
        const aprData = aprRef.current?.getData();
        const udfData = udfRef.current?.getData()?.results;

        const payload = {
            code: code,
            instituton: {
                inventory_setting: inventoryData,
                in_house_apr: aprData,
                user_defined: udfData
            }
        };

        try {
            await createTenantAdminMutation.mutateAsync(payload);
            if (onNext) onNext();
        } catch (error) {
            console.error("Update failed:", error);
        }
    };

    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const { t } = useTranslation();

    // Check if user has permission to view user defined fields
    // For superadmin, we might want to default to true or use the account permissions
    const hasUserDefinedReadPermission = account?.permissions?.settings_user_defined_read === true || true; // Defaulting to true for now if it's for superadmin

    const allTabs = [
        t('institution.inventory'), t('institution.inHouse'), t('institution.security'), t('institution.userDefinedFields'),
    ];

    // Filter tabs based on permission
    const tabs = hasUserDefinedReadPermission ? allTabs : allTabs.filter(tab => tab !== t('institution.userDefinedFields'));

    const [activeTab, setActiveTab] = useState(t('institution.inventory'));

    const getTabDisplayName = (tab) => {
        return tab;
    };

    return (
        <div className="tenant-add-institution w-100">
            <div className="d-flex align-items-center justify-content-between mb-0 tenant-add-institution-tabs">
                <Nav tabs className="border-0 mb-0" style={{ borderBottom: "none" }}>
                            {tabs.map(tab => (
                                <NavItem key={tab}>
                                    <NavLink
                                        href="#"
                                        className={activeTab === tab ? "active font-weight-bold" : "text-muted"}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            position: "relative",
                                            cursor: 'pointer',
                                            color: activeTab === tab ? activeColorStyle.borderColor : undefined,
                                            fontWeight: activeTab === tab ? 700 : 500

                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setActiveTab(tab);
                                        }}
                                    >
                                        {getTabDisplayName(tab)}
                                        {activeTab === tab && (
                                            <div style={{ height: 3, background: activeColorStyle.borderColor, position: "absolute", left: 0, right: 0, bottom: -8 }} />
                                        )}
                                    </NavLink>
                                </NavItem>
                            ))}
                        </Nav>
            </div>
            <TabContent activeTab={activeTab} className="tenant-add-institution-tab-content">
                        <TabPane tabId={activeTab}>
                            {activeTab === t('institution.inventory') && (
                                <SInventoryTabs 
                                    ref={inventoryRef}
                                    permissions={account?.permissions} 
                                    activeColorStyle={activeColorStyle} 
                                    activeColor={activeColor}
                                    isTheme2={isTheme2}
                                    selectStyles={selectStyles}
                                    inputStyle={inputStyle}
                                    inputClassName={inputClassName}
                                    initialData={tenantData?.inventory_setting}
                                    inventoryStatus={tenantData?.inventory_status}
                                />
                            )}
                            {activeTab === t('institution.inHouse') && (
                                <SaprTab 
                                    ref={aprRef}
                                    activeColorStyle={activeColorStyle}
                                    isTheme2={isTheme2}
                                    inputStyle={inputStyle}
                                    inputClassName={inputClassName}
                                    initialData={tenantData?.in_house_apr}
                                />
                            )}
                            {activeTab === t('institution.security') && (
                                <UserDefinedSecurityTabs 
                                    permissions={account?.permissions} 
                                />
                            )}
                            {activeTab === t('institution.userDefinedFields') && (hasUserDefinedReadPermission) && (
                                <SUserDefinedFieldsTabs 
                                    ref={udfRef}
                                    permissions={account?.permissions} 
                                    activeColorStyle={activeColorStyle}
                                    isTheme2={isTheme2}
                                    inputStyle={inputStyle}
                                    inputClassName={inputClassName}
                                    initialData={tenantData?.user_defined ? { results: tenantData.user_defined } : null}
                                />
                            )}
                        </TabPane>
                    </TabContent>
            <div className="d-flex justify-content-end mt-4 gap-2">
                        <div className="mr-auto">
                            <Button
                                color="secondary"
                                outline
                                onClick={onPrev}
                                disabled={createTenantAdminMutation.isPending}
                            >
                                Previous Step
                            </Button>
                        </div>
                        <Button
                            style={{ ...activeColorStyle, color: 'white' }}
                            onClick={handleUpdate}
                            disabled={createTenantAdminMutation.isPending}
                        >
                            {createTenantAdminMutation.isPending ? (
                                <>
                                    <Spinner size="sm" className="mr-2" />
                                    Updating...
                                </>
                            ) : (
                                'Save & Continue'
                            )}
                        </Button>
                        {showNext && (
                            <Button
                                color="secondary"
                                onClick={onNext}
                                disabled={createTenantAdminMutation.isPending}
                            >
                                Next Step
                            </Button>
                        )}
                    </div>
        </div>);
};

export default Institution;
